// components/common/NotificationCenter.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  markAsRead, 
  markAllAsRead,
  deleteAllForRecipient,
  deleteNotificationsByIds
} from '../../services/notificationService';
import { useNavigate } from 'react-router-dom';
import { navigateToDispute } from '../../utils/disputeNavigation';
import { db } from '../../services/firebaseConfig';
import { collection, query, where, orderBy, limit as fsLimit, onSnapshot } from 'firebase/firestore';

const NotificationCenter = ({ isOpen, onClose, mode = 'modal' }) => {
  const { userData: user, userRole } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread, application, dispute
  const [counts, setCounts] = useState({ unreadCount: 0, totalCount: 0 });
  const [priorityFilter, setPriorityFilter] = useState('all'); // all, low, medium, high, urgent
  const [typeFilter, setTypeFilter] = useState('all'); // all, application, dispute, reminder
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Real-time notifications list listener
  useEffect(() => {
    if (!isOpen || !user?.uid) return;

    setLoading(true);

    // Department mapping for team roles
    const roleToDept = {
      PolicyWriter: 'policy',
      FinancialTeam: 'finance',
    };
    const deptRecipientId = roleToDept[userRole] || null;

    // Build base queries: per-user and optionally per-department
    let userQ = query(
      collection(db, 'notifications'),
      where('recipientId', '==', user.uid),
      orderBy('createdAt', 'desc'),
    );
    let deptQ = null;
    if (deptRecipientId) {
      deptQ = query(
        collection(db, 'notifications'),
        where('recipientId', '==', deptRecipientId),
        orderBy('createdAt', 'desc'),
      );
    }

    if (filter === 'unread') {
      userQ = query(
        collection(db, 'notifications'),
        where('recipientId', '==', user.uid),
        where('read', '==', false),
        orderBy('createdAt', 'desc'),
      );
      if (deptRecipientId) {
        deptQ = query(
          collection(db, 'notifications'),
          where('recipientId', '==', deptRecipientId),
          where('read', '==', false),
          orderBy('createdAt', 'desc'),
        );
      }
    }

    userQ = query(userQ, fsLimit(50));
    if (deptQ) deptQ = query(deptQ, fsLimit(50));

    let combined = [];
    let unsubUser = null;
    let unsubDept = null;

    const updateList = () => {
      try {
        let list = [...combined];
        // Remove duplicates by id
        const map = new Map();
        list.forEach((n) => map.set(n.id, n));
        list = Array.from(map.values());
        // Apply client-side type filters
        if (filter === 'application') list = list.filter((n) => n.type === 'application');
        else if (filter === 'dispute') list = list.filter((n) => n.type === 'dispute');
        // Apply advanced filters
        if (typeFilter !== 'all') list = list.filter((n) => (n.type || 'application') === typeFilter);
        if (priorityFilter !== 'all') list = list.filter((n) => (n.priority || 'medium') === priorityFilter);
        if (search.trim()) {
          const q = search.trim().toLowerCase();
          list = list.filter((n) => (
            (n.title || '').toLowerCase().includes(q) ||
            (n.message || '').toLowerCase().includes(q) ||
            (n.applicationId || '').toString().toLowerCase().includes(q)
          ));
        }
        if (dateRange.from) {
          const from = new Date(dateRange.from);
          list = list.filter((n) => new Date(n.createdAt) >= from);
        }
        if (dateRange.to) {
          const to = new Date(dateRange.to);
          // include the whole day
          to.setHours(23,59,59,999);
          list = list.filter((n) => new Date(n.createdAt) <= to);
        }

        // Sort by createdAt desc
        list.sort((a, b) => (new Date(b.createdAt) - new Date(a.createdAt)));
        setNotifications(list);
      } finally {
        setLoading(false);
      }
    };

    unsubUser = onSnapshot(userQ, (snapshot) => {
      const userList = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate?.() || d.data().createdAt,
        readAt: d.data().readAt?.toDate?.() || d.data().readAt,
        expiresAt: d.data().expiresAt?.toDate?.() || d.data().expiresAt,
      }));
      combined = [...userList, ...(combined.filter((n) => n.__source === 'dept') || [])];
      updateList();
    }, (err) => {
      console.error('Notifications listener error (user):', err);
      setLoading(false);
    });

    if (deptQ) {
      unsubDept = onSnapshot(deptQ, (snapshot) => {
        const deptList = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.() || d.data().createdAt,
          readAt: d.data().readAt?.toDate?.() || d.data().readAt,
          expiresAt: d.data().expiresAt?.toDate?.() || d.data().expiresAt,
          __source: 'dept',
        }));
        combined = [...(combined.filter((n) => n.__source !== 'dept') || []), ...deptList];
        updateList();
      }, (err) => {
        console.error('Notifications listener error (dept):', err);
        setLoading(false);
      });
    }

    return () => {
      try { unsubUser && unsubUser(); } catch {}
      try { unsubDept && unsubDept(); } catch {}
    };
  }, [isOpen, user?.uid, userRole, filter, typeFilter, priorityFilter, search, dateRange.from, dateRange.to]);

  // Real-time counts listeners (total + unread)
  useEffect(() => {
    if (!isOpen || !user?.uid) return;

    const totalQ = query(
      collection(db, 'notifications'),
      where('recipientId', '==', user.uid)
    );
    const unreadQ = query(
      collection(db, 'notifications'),
      where('recipientId', '==', user.uid),
      where('read', '==', false)
    );

    const unsubTotal = onSnapshot(totalQ, (snap) => {
      setCounts((c) => ({ ...c, totalCount: snap.size }));
    }, (err) => console.error('Notification total count listener error:', err));

    const unsubUnread = onSnapshot(unreadQ, (snap) => {
      setCounts((c) => ({ ...c, unreadCount: snap.size }));
    }, (err) => console.error('Notification unread count listener error:', err));

    return () => {
      try { unsubTotal && unsubTotal(); } catch {}
      try { unsubUnread && unsubUnread(); } catch {}
    };
  }, [isOpen, user?.uid]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markAsRead(notificationId);
      // listeners will auto-refresh list and counts
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead(user.uid);
      // listeners will auto-refresh list and counts
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleDeleteAll = async () => {
    try {
      if (!user?.uid) return;
      await deleteAllForRecipient(user.uid);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error deleting all notifications:', error);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      if (selectedIds.size === 0) return;
      await deleteNotificationsByIds(Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectMode(false);
    } catch (error) {
      console.error('Error deleting selected notifications:', error);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Navigate to application by role and optional dispute highlight
  const navigateToApplication = (notification) => {
    if (!notification) return;
    const appId = notification.applicationId;
    if (!appId) return;

    // Dispute-specific navigation uses helper to add highlight param
    if (notification.type === 'dispute' && notification.disputeId) {
      navigateToDispute(navigate, appId, notification.disputeId, userRole);
      return;
    }

    // If actionUrl exists, prefer it
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      return;
    }

    // Fallback by role routes
    let path = `/application/${appId}`;
    if (userRole === 'Hospital') path = `/hospital/application/${appId}`;
    else if (userRole === 'Doctor') path = `/doctor/application-review/${appId}`;
    else if (userRole === 'PolicyWriter') path = `/policy/details/${appId}`;
  else if (userRole === 'FinancialTeam') path = `/dashboard/financial/review/${appId}`;
  else if (userRole === 'LeadGenerator') path = `/lead/application-review/${appId}`;
  else if (userRole === 'SuperAdmin') path = `/admin/hospital-details/${appId}`;
  else if (userRole === 'SystemAdmin') path = `/system-admin/dashboard`;
    navigate(path);
  };

  const getNotificationIcon = (type, subType) => {
    if (type === 'application') {
      const icons = {
        submitted: '📋',
        approved: '✅',
        rejected: '❌',
        under_review: '🔍',
        kyp_generated: '📄'
      };
      return icons[subType] || '📄';
    } else if (type === 'dispute') {
      const icons = {
        dispute_raised: '🚨',
        acknowledged: '✅',
        resolved: '✅',
        escalated: '⚠️'
      };
      return icons[subType] || '💬';
    }
    return '🔔';
  };

  const getNotificationColor = (type, read) => {
    if (read) return 'bg-gray-50';
    
    if (type === 'dispute') return 'bg-red-50 border-l-4 border-red-400';
    if (type === 'application') return 'bg-blue-50 border-l-4 border-blue-400';
    return 'bg-yellow-50 border-l-4 border-yellow-400';
  };

  const getTimeAgo = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    const now = new Date();
    const diffInMinutes = Math.floor((now - d) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
  return d.toLocaleDateString();
  };

  // Lock background scroll only for modal mode
  useEffect(() => {
    if (!isOpen || mode !== 'modal') return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isOpen, mode]);

  if (!isOpen) return null;

  if (mode === 'dropdown') {
    return (
      <div className="absolute right-0 mt-2 w-[min(90vw,28rem)] bg-white rounded-xl shadow-2xl border border-gray-200 z-[100] overflow-hidden">
        <div className="flex justify-between items-center p-3 border-b">
          <div className="flex items-center space-x-2">
            <h2 className="text-sm font-semibold text-gray-900">Notifications</h2>
            {counts.unreadCount > 0 && (
              <span className="bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5">
                {counts.unreadCount}
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="border-b">
          <div className="flex border-b overflow-x-auto no-scrollbar px-2">
            {[ 
              { key: 'all', label: 'All' },
              { key: 'unread', label: 'Unread' },
              { key: 'application', label: 'Applications' },
              { key: 'dispute', label: 'Query' }
            ].map(tab => (
              <button key={tab.key} onClick={() => setFilter(tab.key)} className={`px-3 py-2 text-xs font-medium border-b-2 ${filter === tab.key ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-800'}`}>{tab.label}</button>
            ))}
          </div>
          
          {/* Filters and Actions Toolbar for Dropdown */}
          <div className="p-2 bg-gray-50 space-y-1">
            <div className="flex items-center gap-1">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 Search..." className="flex-1 min-w-[100px] border rounded px-2 py-1 text-xs" />
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="border rounded px-1 py-1 text-xs w-[80px]">
                <option value="all">All</option>
                <option value="application">App</option>
                <option value="dispute">Query</option>
              </select>
              <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="border rounded px-1 py-1 text-xs w-[60px]">
                <option value="all">All</option>
                <option value="urgent">⚡</option>
                <option value="high">🔴</option>
              </select>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setSelectMode((s) => !s)} className={`text-xs px-2 py-1 rounded ${selectMode ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>{selectMode ? 'Cancel' : '☐ Select'}</button>
              <button onClick={handleDeleteAll} className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">🗑️ All</button>
              {selectMode && (
                <button onClick={handleDeleteSelected} disabled={selectedIds.size === 0} className={`text-xs px-2 py-1 rounded ${selectedIds.size === 0 ? 'bg-gray-100 text-gray-400' : 'bg-red-500 text-white'}`}>🗑️ {selectedIds.size}</button>
              )}
            </div>
          </div>
        </div>
        <div className="max-h-[70vh] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">No notifications</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {notifications
                .filter((n) => filter !== 'kyp_reassigned' ? true : (n.subType === 'kyp_reassigned'))
                .map((notification) => (
                <div key={notification.id} className={`p-3 hover:bg-gray-50 transition-colors ${getNotificationColor(notification.type, notification.read)} ${selectMode ? 'cursor-default' : 'cursor-pointer'}`}
                  onClick={async () => {
                    if (selectMode) { toggleSelect(notification.id); return; }
                    try { if (!notification.read) { await markAsRead(notification.id); } } catch(e) {}
                    navigateToApplication(notification);
                    onClose && onClose();
                  }}
                >
                  <div className="flex items-start space-x-3">
                    {selectMode && (
                      <input type="checkbox" checked={selectedIds.has(notification.id)} onChange={() => toggleSelect(notification.id)} className="mt-1" />
                    )}
                    <div className="flex-shrink-0 text-xl">{getNotificationIcon(notification.type, notification.subType)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`text-sm font-medium break-words pr-2 ${notification.read ? 'text-gray-600' : 'text-gray-900'}`}>{notification.title}</p>
                        <span className="text-[10px] text-gray-500">{getTimeAgo(notification.createdAt)}</span>
                      </div>
                      <p className={`text-xs mt-1 break-words ${notification.read ? 'text-gray-500' : 'text-gray-700'}`}>{notification.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="p-3 border-t bg-gray-50 text-center text-xs">Showing {notifications.length} of {counts.totalCount}</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-2 sm:p-4">
      <div className="bg-white rounded-2xl w-[min(100vw-1rem,42rem)] sm:w-full sm:max-w-2xl max-h-[85vh] flex flex-col shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100 px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-xl shadow-sm">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Notifications</h2>
                <p className="text-sm text-gray-500">Stay updated with your activities</p>
              </div>
              {counts.unreadCount > 0 && (
                <div className="ml-3 px-3 py-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-semibold rounded-full shadow-lg animate-pulse">
                  {counts.unreadCount} new
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {counts.unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="px-4 py-2 text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 rounded-lg transition-all shadow-sm hover:shadow-md"
                >
                  ✨ Mark all read
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white border-b border-gray-100">
          <div className="flex items-center justify-between px-4 pt-3">
            <div className="flex gap-1 overflow-x-auto no-scrollbar">
              {[ 
                { key: 'all', label: 'All', icon: '📬' },
                { key: 'unread', label: 'Unread', icon: '🆕' },
                { key: 'application', label: 'Applications', icon: '📋' },
                { key: 'dispute', label: 'Query', icon: '💬' },
                { key: 'kyp_reassigned', label: 'KYP', icon: '📄' }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                    filter === tab.key
                      ? 'bg-blue-600 text-white shadow-md scale-105'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Compact Filters Toolbar */}
          <div className="px-4 py-3 space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input 
                  value={search} 
                  onChange={(e) => setSearch(e.target.value)} 
                  placeholder="Search notifications..." 
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" 
                />
              </div>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all">
                <option value="all">📁 All Types</option>
                <option value="application">📋 Application</option>
                <option value="dispute">💬 Query</option>
                <option value="reminder">⏰ Reminder</option>
              </select>
              <div className="relative">
                <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="px-10 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none bg-white">
                  <option value="all">All Priorities</option>
                  <option value="urgent">⚡ Urgent</option>
                  <option value="high">🔴 High</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="low">🟢 Low</option>
                </select>
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <input 
                  type="date" 
                  value={dateRange.from} 
                  onChange={(e) => setDateRange((d) => ({ ...d, from: e.target.value }))} 
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 transition-all" 
                />
                <span className="text-gray-400">→</span>
                <input 
                  type="date" 
                  value={dateRange.to} 
                  onChange={(e) => setDateRange((d) => ({ ...d, to: e.target.value }))} 
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 transition-all" 
                />
              </div>
              
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setSelectMode((s) => !s)} 
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                    selectMode ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {selectMode ? '✓ Cancel' : '☐ Select'}
                </button>
                <button 
                  onClick={handleDeleteAll} 
                  className="px-3 py-2 text-sm font-medium bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-all"
                >
                  🗑️ Delete All
                </button>
                {selectMode && selectedIds.size > 0 && (
                  <button 
                    onClick={handleDeleteSelected} 
                    className="px-3 py-2 text-sm font-medium bg-red-600 text-white hover:bg-red-700 rounded-lg transition-all shadow-md"
                  >
                    Delete {selectedIds.size}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4 7h16M4 12h12M4 17h8" />
              </svg>
              <p>No notifications found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications
                .filter((n) => filter !== 'kyp_reassigned' ? true : (n.subType === 'kyp_reassigned'))
                .map((notification) => (
                <div
                  key={notification.id}
                  className={`group relative p-4 ${selectMode ? 'cursor-default' : 'cursor-pointer'} transition-all duration-200 ${getNotificationColor(notification.type, notification.read)} ${!notification.read ? 'border-l-4 border-blue-500 bg-blue-50/50' : 'bg-white hover:bg-gray-50'}`}
                  onClick={async () => {
                    if (selectMode) { toggleSelect(notification.id); return; }
                    try {
                      if (!notification.read) {
                        await markAsRead(notification.id);
                      }
                    } catch (e) {
                      // ignore
                    } finally {
                      navigateToApplication(notification);
                      onClose && onClose();
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    {selectMode && (
                      <div className="mt-0.5">
                        <input 
                          type="checkbox" 
                          checked={selectedIds.has(notification.id)} 
                          onChange={() => toggleSelect(notification.id)} 
                          className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" 
                        />
                      </div>
                    )}
                    <div className={`flex-shrink-0 p-2 rounded-xl ${notification.read ? 'bg-gray-100' : 'bg-gradient-to-br from-blue-100 to-indigo-100'}`}>
                      <div className="text-2xl">{getNotificationIcon(notification.type, notification.subType)}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className={`text-sm font-semibold ${notification.read ? 'text-gray-600' : 'text-gray-900'}`}>
                          {notification.title}
                        </p>
                        <div className="flex items-center gap-2">
                          {!notification.read && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                          )}
                          <span className="text-xs text-gray-500 font-medium">
                            {getTimeAgo(notification.createdAt)}
                          </span>
                        </div>
                      </div>
                      <p className={`text-sm leading-relaxed ${notification.read ? 'text-gray-500' : 'text-gray-700'}`}>
                        {notification.message}
                      </p>
                      {notification.metadata && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {notification.metadata.patientName && (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                              👤 {notification.metadata.patientName}
                            </span>
                          )}
                          {notification.applicationId && (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs bg-gray-100 text-gray-700 border border-gray-200 font-medium">
                              📋 {notification.applicationId}
                            </span>
                          )}
                          {notification.priority && notification.priority !== 'medium' && (
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-semibold ${
                              notification.priority === 'urgent'
                                ? 'bg-red-100 text-red-800 border border-red-200'
                                : notification.priority === 'high'
                                ? 'bg-orange-100 text-orange-800 border border-orange-200'
                                : notification.priority === 'low'
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                            }`}>
                              ⚡ {notification.priority.toUpperCase()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              <span className="font-medium">{counts.unreadCount} unread</span>
            </div>
            <div className="h-4 w-px bg-gray-300"></div>
            <div className="text-sm text-gray-600">
              Showing <span className="font-semibold text-gray-900">{notifications.length}</span> of <span className="font-semibold text-gray-900">{counts.totalCount}</span> notifications
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;
